import { useState } from "react";
import { authOwnerLogin } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, LogIn, Loader2 } from "lucide-react";

/**
 * LoginGate — shown when AuthContext has user=null (anonymous + demo mode
 * disabled). Accepts owner email + password; on success, AuthContext.refresh
 * is called so the rest of the app mounts behind the valid session cookie.
 */
export default function LoginGate() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter email and password");
      return;
    }
    setLoading(true);
    try {
      await authOwnerLogin(email.trim(), password);
      toast.success("Welcome back");
      await refresh();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error("Too many failed attempts", {
          description: detail || "Try again in 15 minutes",
        });
      } else {
        toast.error("Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-slate-900 via-[#0a1738] to-[#1b0c3d]"
      data-testid="login-gate"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,47,167,0.25),transparent_50%),radial-gradient(circle_at_80%_70%,rgba(191,53,239,0.18),transparent_50%)] pointer-events-none"
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white/95 backdrop-blur-sm p-8 shadow-2xl border border-white/20">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#002FA7] grid place-items-center text-white">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-widest text-slate-500">
              Profit10x · AI Revenue Engine
            </div>
            <div className="text-xl font-extrabold text-slate-900 leading-tight">
              Owner sign-in
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Your Sales OS
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label
              htmlFor="login-email"
              className="block text-[12px] font-semibold text-slate-600 mb-1"
            >
              Email
            </label>
            <Input
              id="login-email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@profit10x.ai"
              data-testid="login-email-input"
              className="h-11"
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="block text-[12px] font-semibold text-slate-600 mb-1"
            >
              Password
            </label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              data-testid="login-password-input"
              className="h-11"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-md bg-[#002FA7] hover:bg-[#002072] text-white font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-2" />
                Sign in
              </>
            )}
          </Button>
        </form>

        <p className="mt-5 text-[12px] text-slate-500 text-center">
          Private access only. Credentials are held by the workspace owner.
        </p>
      </div>
    </div>
  );
}
